import { useMutation, useQuery } from 'react-query';
import { getNotifications } from '../services/query';
import { useMemo, useRef, useState } from 'react';
import { Notifications } from '../types';
import { createNotification, deleteNotification, updateNotification } from '../services/mutations';
import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { getTemplateTags } from 'graphql/templates/queries';
import { uniqueById } from 'utils/unique-by-id';

export type ConfirmationModal = {
	openConfirmation: boolean;
	id: string;
};

const useEmailTemplates = () => {
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModal>({
    openConfirmation: false,
    id: '',
  });
  const [open, setOpen] = useState(false);
  const showAlert = useAlert();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notifications[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<Notifications>();

  const bySubject = (x: Notifications, y: Notifications) => x.subject.localeCompare(y.subject);
  const updateInFlight = useRef(false);
  const deleteInFlight = useRef(false);
  const createInFlight = useRef(false);

  const {
    isLoading,
    isFetching,
    isError: isNotificationsError,
    error: notificationsError,
    refetch: refetchNotifications,
  } = useQuery(['notifications'], getNotifications, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        setNotifications([...data.getNotifications].sort(bySubject));
      }
    },
  });

  const { data: templateTagsResponse } = useQuery(['templateTags'], getTemplateTags, {
    refetchOnWindowFocus: false,
  });

  const tagsField = useMemo(() => {
    if (templateTagsResponse && templateTagsResponse.getTemplateTags) {
      return uniqueById(templateTagsResponse.getTemplateTags.filter((tag) => tag.kind === 'text'));
    }
    return [];
  }, [templateTagsResponse]);

  const { mutate: updateNotificationMutation, ...updateNotificationResponse } = useMutation(
    updateNotification,
    {
      onSuccess: (data) => {
        if (data) {
          const updated = data.updateNotification;
          setNotifications((prev) =>
            prev.map((n) => (n._id === updated._id ? updated : n)).sort(bySubject)
          );
          setSelectedNotification((prev) => (prev && prev._id === updated._id ? updated : prev));
          showAlert(t('emailTemplates:notification-updated'), 'success');
        }
      },
      onSettled: () => {
        updateInFlight.current = false;
      },
    }
  );

  const { mutate: deleteNotificationMutation, ...deleteNotificationResponse } = useMutation(
    deleteNotification,
    {
      onSuccess: (data) => {
        if (data) {
          const deletedId = data.deleteNotification._id;
          setNotifications((prev) => prev.filter((n) => n._id !== deletedId).sort(bySubject));
          setSelectedNotification((prev) => (prev && prev._id === deletedId ? undefined : prev));
          showAlert(t('emailTemplates:notification-deleted'), 'success');
        }
        setConfirmationModal({ openConfirmation: false, id: '' });
      },
      onError: () => {
        setConfirmationModal({ openConfirmation: false, id: '' });
      },
      onSettled: () => {
        deleteInFlight.current = false;
      },
    }
  );

  const { mutate: createNotificationMutation, ...createNotificationResponse } = useMutation(
    createNotification,
    {
      onSuccess: (data) => {
        if (data) {
          setNotifications((prev) => [...prev, data.createNotification].sort(bySubject));
          showAlert(t('emailTemplates:notification-created'), 'success');
        }
        setConfirmationModal({ openConfirmation: false, id: '' });
        setOpen(false);
      },
      onSettled: () => {
        createInFlight.current = false;
      },
    }
  );

  const handleUpdateCreateNotification = (data) => {
    if (data.id !== '') {
      if (updateInFlight.current) return;
      updateInFlight.current = true;
      updateNotificationMutation(data);
    } else {
      if (createInFlight.current) return;
      createInFlight.current = true;
      delete data.id;
      createNotificationMutation(data);
    }
  };

  const handleDeleteNotification = () => {
    if (deleteInFlight.current) return;
    deleteInFlight.current = true;
    const { id } = confirmationModal;
    deleteNotificationMutation({ id });
  };

  const isCreating = createNotificationResponse.isLoading;
  const isUpdating = updateNotificationResponse.isLoading;
  const isDeleting = deleteNotificationResponse.isLoading;

  return {
    notifications,
    isLoading: isLoading || isCreating || isDeleting || isUpdating,
    isLoadingNotifications: isLoading,
    isFetching,
    isNotificationsError,
    notificationsError,
    refetchNotifications,
    isCreating,
    isUpdating,
    isDeleting,
    isSubmitting: isCreating || isUpdating || isDeleting,
    setSelectedNotification,
    selectedNotification,
    handleUpdateCreateNotification,
    confirmationModal,
    setConfirmationModal,
    handleDeleteNotification,
    t,
    open,
    setOpen,
    tagsField,
  };
};

export default useEmailTemplates;
